import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import {
  ANSRequestModel,
  NotificationRequestCustomerEmail,
  NotificationRequestInternalEmail,
  OldReplaceVehicleQuoteApiRequest,
  ReplaceVehicleQuoteApiRequest,
  SubmitOnlineBindRequestModel
} from '@amfam/policy/models/lib/models/replace-vehicle';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';
import { CommunicationService } from '@amfam/shared/utility/shared-services/lib/communication/communication.service';

import {
  replaceVehicleANS,
  replaceVehicleANSFail,
  replaceVehicleANSNotifyAgent,
  replaceVehicleANSNotifyAgentFail,
  replaceVehicleANSNotifyAgentSuccess,
  replaceVehicleANSSuccess,
  replaceVehicleGetPremium,
  replaceVehicleGetPremiumFail,
  replaceVehicleGetPremiumSuccess,
  replaceVehicleNotifyAgent,
  replaceVehicleNotifyAgentFail,
  replaceVehicleNotifyAgentSuccess,
  replaceVehicleNotifyCustomer,
  replaceVehicleNotifyCustomerFail,
  replaceVehicleNotifyCustomerSuccess,
  replaceVehicleSubmitOnlineBind,
  replaceVehicleSubmitOnlineBindFail,
  replaceVehicleSubmitOnlineBindSuccess,
  sendAnalytics,
  sendGracePeriodCustomerEmail,
  sendGracePeriodCustomerEmailFailed,
  sendGracePeriodCustomerEmailSuccess
} from './replace-vehicle.actions';
import { ReplaceVehicleService } from './replace-vehicle.service';

export const getPremiumEstimate = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(replaceVehicleGetPremium),
      map(action => action.payload),
      switchMap((payload: ReplaceVehicleQuoteApiRequest | OldReplaceVehicleQuoteApiRequest) =>
        replaceVehicleService.getPremiumEstimate(payload).pipe(
          map(response => {
            if (response?.status?.code?.toString() === '203000') {
              return replaceVehicleGetPremiumFail(response);
            } else {
              return replaceVehicleGetPremiumSuccess(response);
            }
          }),
          catchError(error => of(replaceVehicleGetPremiumFail(error)))
        )
      )
    );
  },
  { functional: true }
);

export const submitQuoteForBind = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(replaceVehicleSubmitOnlineBind),
      map(action => action.payload),
      switchMap((payload: SubmitOnlineBindRequestModel) =>
        replaceVehicleService.submitOnlineBind(payload).pipe(
          mergeMap(response => [
            fromRouterActions.Go({
              path: ['/policies/replace-vehicle/' + payload.vehicleId + '/confirmation']
            }),
            replaceVehicleSubmitOnlineBindSuccess(response)
          ]),
          catchError(error => of(replaceVehicleSubmitOnlineBindFail(error)))
        )
      )
    );
  },
  { functional: true }
);

export const emailAgent = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(replaceVehicleNotifyAgent),
      map(action => action.payload),
      switchMap((payload: NotificationRequestInternalEmail) =>
        replaceVehicleService.generateInternalEmails(payload).pipe(
          mergeMap(() => {
            const updateReplaceVehicleNextActions: Action[] = [];
            /**
             * AS: If we do not have a quote we are not triggering an ANS notification and need to route to
             * confirmation from this effect.
             */
            if (payload.type === 'No Quote') {
              updateReplaceVehicleNextActions.push(
                fromRouterActions.Go({
                  path: ['/policies/replace-vehicle/' + payload.vehicleId + '/confirmation']
                })
              );
            }
            updateReplaceVehicleNextActions.push(replaceVehicleNotifyAgentSuccess(payload));
            return updateReplaceVehicleNextActions;
          }),
          catchError(error => of(replaceVehicleNotifyAgentFail(error)))
        )
      )
    );
  },
  { functional: true }
);

export const emailCustomer = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(replaceVehicleNotifyCustomer),
      map(action => action.payload),
      switchMap((payload: NotificationRequestCustomerEmail) =>
        replaceVehicleService
          .generateCustomerEmail(
            payload.email,
            replaceVehicleService.buildMessageDetails(payload.message),
            payload.templateId
          )
          .pipe(
            map(response => replaceVehicleNotifyCustomerSuccess(response)),
            catchError(error => of(replaceVehicleNotifyCustomerFail(error)))
          )
      )
    );
  },
  { functional: true }
);

export const ansNotification = createEffect(
  (
    actions$ = inject(Actions),
    replaceVehicleService = inject(ReplaceVehicleService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(replaceVehicleANS),
      map(action => action.payload),
      switchMap((payload: ANSRequestModel) =>
        replaceVehicleService.ansNotification(payload).pipe(
          map(_response => {
            store.dispatch(
              fromRouterActions.Go({
                path: ['/policies/replace-vehicle/' + payload.vehicleId + '/confirmation']
              })
            );
            return replaceVehicleANSSuccess();
          }),
          catchError(error => of(replaceVehicleANSFail(error)))
        )
      )
    );
  },
  { functional: true }
);

export const ansNotificationForBind = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(replaceVehicleANSNotifyAgent),
      map(action => action.payload),
      switchMap((payload: ANSRequestModel) =>
        replaceVehicleService
          .buildANSNotificationPayload(
            payload.producerId,
            payload.policyNumber,
            payload.notificationText,
            payload.orderNumber
          )
          .pipe(
            switchMap(ansPayload =>
              // FIXME: This route is executed after replace vehicle...
              replaceVehicleService.ansNotificationForBind(ansPayload).pipe(
                mergeMap(() => [
                  payload.vehicleId
                    ? fromRouterActions.Go({
                        path: ['/policies/replace-vehicle/' + payload.vehicleId + '/confirmation']
                      })
                    : fromRouterActions.Go({
                        path: ['/policies/risk-selection/confirmation']
                      }),
                  replaceVehicleANSNotifyAgentSuccess()
                ]),
                catchError(error => of(replaceVehicleANSNotifyAgentFail(error)))
              )
            )
          )
      )
    );
  },
  { functional: true }
);

/**
 * @author: Abhishek Singh
 * @returns:
 * @description: This effect triggers a analytics notification.
 */
export const analyticsNotification = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(sendAnalytics),
      map(action => action.payload),
      map(payload => replaceVehicleService.sendAnalytics(payload))
    );
  },
  { dispatch: false, functional: true }
);

export const sendCustomerEmailNotification = createEffect(
  (actions$ = inject(Actions), communicationService = inject(CommunicationService)) => {
    return actions$.pipe(
      ofType(sendGracePeriodCustomerEmail),
      map(action => action.payload),
      switchMap(templatedEmail =>
        communicationService.sendTemplatedEmail(templatedEmail).pipe(
          map(() => sendGracePeriodCustomerEmailSuccess()),
          catchError(() => of(sendGracePeriodCustomerEmailFailed()))
        )
      )
    );
  },
  { functional: true }
);
